import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CheckContentService, CheckData } from '../check-content-service';
import { StartCheckContentApi } from '../../apis/start-check-content-api';
import { GetCheckContentApi, type GetCheckContentIssue, type GetCheckContentResponse } from '../../apis/get-check-content-api';
import type { SpecChapterId, ParagraphIssueGroup, SpecChapterParagraphIssue } from '../../types';
import _ from 'lodash';

interface CheckContentConfig {
    pollingIntervalMs: number;
}

@injectable()
export class CheckContentServiceImpl implements CheckContentService {
    private readonly _config: CheckContentConfig = {
        pollingIntervalMs: 2000,
    };

    constructor(
        @inject(StartCheckContentApi)
        private _startCheckContentApi: StartCheckContentApi,
        @inject(GetCheckContentApi)
        private _getCheckContentApi: GetCheckContentApi,
    ) {}

    private async _startCheck(data: CheckData, abortSignal?: AbortSignal): Promise<void> {
        await this._startCheckContentApi.startCheckContent({
            task_id: data.taskId,
            chapter_type: data.chapterType,
            regenerate: data.regenerate,
        }, abortSignal);
    }

    async getParagraphIssueGroup(options: CheckData, abortSignal?: AbortSignal): Promise<ParagraphIssueGroup[]> {
        const { taskId, chapterType, regenerate } = options;

        try {
            // 先启动检查
            await this._startCheck({
                taskId,
                chapterType,
                regenerate,
            }, abortSignal);

            // 使用传入的 abortSignal 或创建新的 AbortController
            const abortController = abortSignal ? { signal: abortSignal } : new AbortController();

            const response = await this._pollUntilCompleted(taskId, chapterType, abortController);
            return this._transformResponseToParagraphIssues(response, chapterType);
        } catch (error) {
            // 如果是取消操作导致的错误，返回空数组
            if (error instanceof Error && error.name === 'AbortError') {
                return [];
            }
            throw error;
        }
    }

    private async _pollUntilCompleted(taskId: string, chapterType: string, abortController: AbortController | { signal: AbortSignal }) {
        let response = await this._getCheckContentApi.getCheckContent({
            task_id: taskId,
            chapter_type: chapterType,
        }, abortController.signal);

        // 轮询直到状态为 completed
        while (response.status === 'running') {
            // 等待配置的间隔时间后重新请求，支持 abort 中断
            await this._delay(this._config.pollingIntervalMs, abortController.signal);

            response = await this._getCheckContentApi.getCheckContent({
                task_id: taskId,
                chapter_type: chapterType,
            }, abortController.signal);
        }

        return response;
    }

    private _delay(ms: number, abortSignal: AbortSignal): Promise<void> {
        return new Promise((resolve, reject) => {
            // 如果已经被中断，立即拒绝
            if (abortSignal.aborted) {
                const error = new Error('AbortError');
                error.name = 'AbortError';
                reject(error);
                return;
            }

            let isResolved = false;

            const timeoutId = setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    abortSignal.removeEventListener('abort', onAbort);
                    resolve();
                }
            }, ms);

            // 监听 abort 事件，立即中断等待
            const onAbort = () => {
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(timeoutId);
                    const error = new Error('AbortError');
                    error.name = 'AbortError';
                    reject(error);
                }
            };

            abortSignal.addEventListener('abort', onAbort, { once: true });
        });
    }

    private _transformResponseToParagraphIssues(response: GetCheckContentResponse, chapterType: SpecChapterId): ParagraphIssueGroup[] {
        return _.map(response.paragraphs, (paragraph: GetCheckContentResponse['paragraphs'][number]): ParagraphIssueGroup => ({
            chapterId: chapterType,
            paragraphIndex: paragraph.paragraph_number,
            issues: _.map(paragraph.problem_list, (problem: GetCheckContentIssue): SpecChapterParagraphIssue => ({
                id: problem.id,
                chapterType: chapterType,
                paragraphIndex: paragraph.paragraph_number,
                errorType: problem.check_rule_desc,
                errorDescription: problem.reason,
                errorContent: problem.origin_text,
                errorReference: problem.check_url,
                isIgnored: false,
                originalData: problem,
            })),
            isIgnored: false,
            isResolved: false,
            replaceContents: [],
            currentReplaceIndex: 0,
        })).filter((issue: any) => issue.issues.length > 0);
    }
}
